import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Link from "@mui/material/Link";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import type { DriverRow } from "@/types/driver";
import { ContextField, ContextText } from "./ContextBlock";
import { NO_VALUE } from "./contextFormat";

/** The Google Maps search page for a device fix, in a new tab. */
export function googleMapsUrl(latitude: number, longitude: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}

/**
 * The driver's nearest city and state as "City, ST", with the country under it and, when the device reported a fix,
 * a link that opens it in Google Maps so a dispatcher can see where the driver is rather than read it.
 *
 * The state is not repeated under the city: the row's `location` already ends in it.
 */
export default function LocationField({ driver }: { driver: DriverRow }) {
  const mapsUrl =
    driver.latitude != null && driver.longitude != null ? googleMapsUrl(driver.latitude, driver.longitude) : null;
  return (
    <ContextField
      icon={<LocationOnOutlinedIcon sx={{ fontSize: 18 }} />}
      label="Location"
      testId={TestIds.dispatch.contextLocation}
    >
      <ContextText variant="value">{driver.location || NO_VALUE}</ContextText>
      {(driver.country || mapsUrl) && (
        <ContextText variant="subtext">
          {driver.country}
          {driver.country && mapsUrl ? " · " : ""}
          {mapsUrl && (
            <Link
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: "var(--color-brand-primary-400)" }}
            >
              Google Maps
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
          )}
        </ContextText>
      )}
    </ContextField>
  );
}
